import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Input, Label, Textarea } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast, Toaster } from "sonner";
import { Plus, X, Search, AlertTriangle, LayoutGrid, TrendingUp, Sparkles } from "lucide-react";

const sevMap = { critical: "critical", high: "warning", medium: "warning", low: "success" };
const statusMap = { open: "critical", mitigating: "info", accepted: "warning", closed: "neutral" };
const CATEGORIES = ["Technical", "Access Control", "Third-party", "Governance", "Data Protection", "Human", "Compliance", "Financial", "Operational"];

export default function RisksPageV2() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [controls, setControls] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const empty = {
    title: "", description: "", category: "Technical",
    likelihood: 3, impact: 3, owner_id: "",
    controls_mitigation: [],
    business_impact: { financial: "medium", reputational: "medium", operational: "medium" },
  };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/risks");
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    (async () => {
      const [c, u] = await Promise.all([
        api.get("/compliance/controls"),
        api.get("/users?limit=200").catch(() => ({ data: { items: [] } })),
      ]);
      setControls(c.data.items || []);
      setUsers(u.data.items || []);
    })();
  }, [load]);

  const filtered = items.filter((r) => !q || (r.title + " " + r.category).toLowerCase().includes(q.toLowerCase()));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, likelihood: Number(form.likelihood), impact: Number(form.impact) };
      if (!payload.owner_id) delete payload.owner_id;
      await api.post("/risks", payload);
      toast.success("Risk logged");
      setShowForm(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const runAutoGen = async () => {
    const assessId = window.prompt("Enter assessment ID to auto-derive risks from failures:");
    if (!assessId) return;
    try {
      const { data } = await api.post(`/risks/auto-from-assessment?assessment_id=${assessId}`);
      toast.success(`Created ${data.risks_created} risks`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const scoreOf = (r) => (r.likelihood || 0) * (r.impact || 0);

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Risk Register</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Enterprise risk register with Likelihood × Impact scoring, mitigation controls, and residual risk.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/risk-matrix")} data-testid="risk-matrix-btn">
            <LayoutGrid className="w-4 h-4" /> Heat map
          </Button>
          <Button variant="outline" onClick={() => navigate("/risk-trends")} data-testid="risk-trends-btn">
            <TrendingUp className="w-4 h-4" /> Trends
          </Button>
          <Button variant="outline" onClick={runAutoGen} data-testid="risk-autogen-btn">
            <Sparkles className="w-4 h-4" /> Auto-generate
          </Button>
          <Button data-testid="risk-new" onClick={() => { setForm(empty); setShowForm(true); }}>
            <Plus className="w-4 h-4" /> New risk
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="risks-search" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" placeholder="Search risks…" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="border-t border-b border-border">
                  <th className="text-left py-2.5 px-4 label-eyebrow">Risk</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Category</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">L × I</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Inherent</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Residual</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Status</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Owner</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Loading…</td></tr>}
                {!loading && filtered.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">No risks.</td></tr>}
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`risk-row-${r.id}`}
                      onClick={() => navigate(`/risks/${r.id}`)}
                      className="border-b border-border hover:bg-muted/40 cursor-pointer transition-colors">
                    <td className="py-3 px-4 font-medium max-w-xs truncate">{r.title}</td>
                    <td className="py-3 px-4"><Badge variant="neutral">{r.category}</Badge></td>
                    <td className="py-3 px-4 tabular-nums">
                      <span className="inline-flex items-center gap-1 text-xs">
                        <span className="text-muted-foreground">L</span>{r.likelihood || "—"}
                        <span className="text-muted-foreground">×</span>
                        <span className="text-muted-foreground">I</span>{r.impact || "—"}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant={sevMap[r.severity]}>{r.inherent_score ?? scoreOf(r)} · {r.severity}</Badge>
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant={sevMap[r.residual_severity]}>{r.residual_score} · {r.residual_severity}</Badge>
                    </td>
                    <td className="py-3 px-4"><Badge variant={statusMap[r.status] || "neutral"}>{r.status}</Badge></td>
                    <td className="py-3 px-4 text-muted-foreground">{r.owner_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto card-surface animate-fade-in" onClick={(e) => e.stopPropagation()} data-testid="risk-form-modal">
            <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-card">
              <h3 className="text-base font-semibold">New risk</h3>
              <button onClick={() => setShowForm(false)} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Title</Label>
                <Input data-testid="risk-form-title" className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
              </div>
              <div className="md:col-span-2">
                <Label>Description</Label>
                <Textarea className="mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <Label>Category</Label>
                <select className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm mt-1.5"
                        value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label>Owner</Label>
                <select className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm mt-1.5"
                        value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value })}>
                  <option value="">Assign to me</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Likelihood: <span className="tabular-nums">{form.likelihood}</span></Label>
                <input data-testid="risk-form-likelihood" type="range" min="1" max="5" step="1" className="w-full mt-1.5"
                       value={form.likelihood} onChange={(e) => setForm({ ...form, likelihood: Number(e.target.value) })} />
                <div className="text-xs text-muted-foreground flex justify-between"><span>Rare</span><span>Almost certain</span></div>
              </div>
              <div>
                <Label>Impact: <span className="tabular-nums">{form.impact}</span></Label>
                <input data-testid="risk-form-impact" type="range" min="1" max="5" step="1" className="w-full mt-1.5"
                       value={form.impact} onChange={(e) => setForm({ ...form, impact: Number(e.target.value) })} />
                <div className="text-xs text-muted-foreground flex justify-between"><span>Insignificant</span><span>Catastrophic</span></div>
              </div>
              <div className="md:col-span-2 rounded-md border border-border p-3 bg-muted/40">
                <div className="label-eyebrow">Computed risk score</div>
                <div className="mt-1 text-3xl font-semibold tabular-nums">{form.likelihood * form.impact}</div>
                <div className="text-xs text-muted-foreground">Likelihood × Impact = score</div>
              </div>
              <div className="md:col-span-2">
                <Label>Business impact</Label>
                <div className="grid grid-cols-3 gap-2 mt-1.5">
                  {["financial", "reputational", "operational"].map((k) => (
                    <div key={k}>
                      <div className="text-xs text-muted-foreground capitalize mb-1">{k}</div>
                      <select className="h-9 w-full rounded-md border border-border bg-card px-2 text-xs"
                              value={form.business_impact[k]}
                              onChange={(e) => setForm({ ...form, business_impact: { ...form.business_impact, [k]: e.target.value } })}>
                        {["low", "medium", "high"].map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Mitigating controls</Label>
                <select multiple value={form.controls_mitigation}
                        onChange={(e) => setForm({ ...form, controls_mitigation: Array.from(e.target.selectedOptions).map((o) => o.value) })}
                        className="mt-1.5 w-full rounded-md border border-border bg-card px-3 text-sm min-h-[80px]">
                  {controls.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 sticky bottom-0 bg-card pt-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" data-testid="risk-form-submit"><AlertTriangle className="w-4 h-4" /> Log risk</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
